import { faTag, faDownload } from '@fortawesome/fontawesome-free-solid';
import * as debounce from 'debounce';
import { always, path, pipe } from 'ramda';
import React, { CSSProperties as css, useState } from 'react';
import { connect, DispatchProp, useSelector } from 'react-redux';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { ReactComponent as HDMIIcon } from '../../assets/svg/hdmi-icon.svg';
import { ReactComponent as MapIcon } from '../../assets/svg/map-icon.svg';
import { ReactComponent as MoreIcon } from '../../assets/svg/more-icon.svg';
import { ReactComponent as PeopleIcon } from '../../assets/svg/people-icon.svg';
import { ReactComponent as TagsIcon } from '../../assets/svg/tags-icon.svg';
import { ReactComponent as PdfIcon } from '../../assets/svg/pdf.svg';

import { test, toLower } from 'ramda';
import { GoogleSearchService } from '../../shared/googleSearchService';
import UserImage from '../general/UserImage';

import { BaseComponent } from '../../shared/BaseComponent';
import {
  devicesMatchSelector,
  extraInfoDevicesMatchSelector,
  extraInfoPeopleMatchSelector,
  peopleMatchSelector,
  tagsMatchSelector,
} from '../../shared/db/search-selectors';
import * as tagSelectors from '../../shared/db/tags-labels-selectors';
import { extractDevice, gstractLocation, isMobile, vals } from '../../shared/helpers';
import {
  ExtraInfoFields,
  IconTypes,
  iDeviceDetails,
  iExtraInfo,
  iFullStoreState,
  iList,
  iLocation,
  iPerson,
  iTag,
  ItemType
} from '../../shared/interfaces';
import { fullCloseDashboard } from "../../stores/reducers/dashboardInfo/AC";
import {
  DevicesDetailsContainer,
  DevicesLastPingContainer,
} from '../../stores/reducers/devicesData';
import { Actions as GeneralActions } from '../../stores/reducers/general-reducers';
import { SearchOptions } from '../../stores/reducers/general-reducers-fn';
import {
  getMapCenter as getMapZoomFromStore,
  Actions as GmapActions,
} from '../../stores/reducers/gmap-reducers';
import { Actions as ReportActions } from '../../stores/reducers/report-reducers';
import { Fa } from '../elements/fa';
import DeviceListItem from '../pages/devices/DeviceListItem';
import SvgIcon, { IconSize } from '../SvgIcon/SvgIcon';
import SearchItemTile from './SearchItemTile/SearchItemTile';
import { Collapsible } from '../general/Collapsible';
import { Button, ButtonStyles, ButtonSize } from '../Button';

import styles from './search.module.scss';
import ModalImage from '../ModalImage';
import { isSpecialType } from '../elements/ExtraInfo/extraInfo.utils';
import { ExtraInfoValue } from '../elements/ExtraInfo/types';

const width = 450;
const ADVANCED_SEARCH_PREFIX = '__advanced_search__:';

type AdvancedSearchCondition = {
  id: string;
  fieldId: string;
  fieldLabel: string;
  operator: 'startsWith' | 'contains' | 'greaterThan' | 'lessThan' | 'equals';
  value: string;
};

type iPropsFromStore = {
  // from store
  isSearchVis: boolean;
  isBookmarksSearchVis: boolean;
  searchTerm: string;
  searchBy: SearchOptions;
  devicesDetails: DevicesDetailsContainer;
  devicesLastPing: DevicesLastPingContainer;

  // calculated
  devices: iList<iDeviceDetails>;
  people: iList<iPerson>;
  tags: iList<iTag>;
  extraInfoDevices: iList<iDeviceDetails>;
  extraInfoPeople: iList<iPerson>;
  getMapCenter: () => iLocation;
  currentLabels: iList<iExtraInfo>;
  getLabelsForDevice: (deviceId: string) => Array<[string, string]>;
};

type iState = {
  searching: boolean;
  gSearchResults: google.maps.places.PlaceResult[];
  imageSrc: string | null;
  cachedDevices: iList<iDeviceDetails>;
  cachedPeople: iList<iPerson>;
  cachedTags: iList<iTag>;
  cachedExtraInfoDevices: iList<iDeviceDetails>;
  cachedExtraInfoPeople: iList<iPerson>;
  cachedDevicesLastPing: DevicesLastPingContainer;
  lastSearchTerm: string;
};

type iProps = DispatchProp & iPropsFromStore & RouteComponentProps;
const mapStateToProps = (state: iFullStoreState): iPropsFromStore => {
  const getMapCenter = () => getMapZoomFromStore(state.gmap);

  return {
    isSearchVis: state.general.showSearch,
    isBookmarksSearchVis: state.general.showBookmarksSearch,
    devices: devicesMatchSelector(state),
    people: peopleMatchSelector(state),
    tags: tagsMatchSelector(state),
    extraInfoDevices: extraInfoDevicesMatchSelector(state),
    extraInfoPeople: extraInfoPeopleMatchSelector(state),
    devicesDetails: state.devicesData.devicesDetails,
    devicesLastPing: state.devicesData.devicesLastPing,
    searchTerm: state.general.searchTerm,
    searchBy: state.general.searchBy,
    getMapCenter,
    currentLabels: state.general.extraInfo,
    getLabelsForDevice: (deviceId: string) => tagSelectors.getItemLabelNameValMap(state, { type: ItemType.device, itemId: deviceId }),
  };
};

export const getDeviceMakeAndModel = (
  currentLabels: Array<[string, string]>
) => {
  let make = '';
  let model = '';

  currentLabels.forEach(([name, value]) => {
    if (name === ExtraInfoFields.MAKE) {
      make = value || '';
    }
    if (name === ExtraInfoFields.MODEL) {
      model = value || '';
    }
  });

  return {
    make,
    model,
    combined: `${make} ${model}`.trim(),
  };
};

class SearchModal extends BaseComponent<iProps, iState> {
  state: iState = {
    gSearchResults: [],
    searching: false,
    imageSrc: null,
    cachedDevices: {},
    cachedPeople: {},
    cachedTags: {},
    cachedExtraInfoDevices: {},
    cachedExtraInfoPeople: {},
    cachedDevicesLastPing: null as any,
    lastSearchTerm: '',
  };

  private searchModalRef = React.createRef<HTMLDivElement>();

  curSearch;

  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this._performSearch && this._performSearch.clear) {
      this._performSearch.clear();
    }
  }

  _performSearch = debounce(async (keyword) => {
    const { getMapCenter } = this.props;

    const search = (this.curSearch = GoogleSearchService.get(
      keyword,
      getMapCenter()
    ));

    const res = await this.curSearch;

    if (!this._isMounted) return;

    if (this.curSearch !== search) return; // new search fired

    this.setState({ searching: false, gSearchResults: res || [] });
  }, 800);

  private mapSearch = (keyword) => {
    this.setState({ searching: true });
    this._performSearch(keyword);
  };

  componentWillReceiveProps(props) {
    if (props.searchTerm !== this.props.searchTerm) {
      this.resetResultsScrollToTop();
      this.mapSearch(props.searchTerm);
      // Cache search results when term changes
      this.setState({
        cachedDevices: props.devices,
        cachedPeople: props.people,
        cachedTags: props.tags,
        cachedExtraInfoDevices: props.extraInfoDevices,
        cachedDevicesLastPing: props.devicesLastPing,
        cachedExtraInfoPeople: props.extraInfoPeople,
        lastSearchTerm: props.searchTerm,
      });
    }
  }

  private resetResultsScrollToTop = () => {
    const root = this.searchModalRef.current;
    const scrollContainer = root?.closest('.scrollable') as HTMLElement | null;

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0 });
      return;
    }

    root?.scrollTo?.({ top: 0 });
  }

  recenterMap = (center: iLocation): void => {
    this.props.dispatch(GmapActions.RECENTER_MAP(center));
  };

  handleResetImageClick = (e) => {
    e.stopPropagation();
    this.setState({ imageSrc: null });
  }

  handleImageClick = (event, src) => {
    event.stopPropagation();
    this.setState({ imageSrc: src });
  }

  isSpecialType = (value: any, key: string) => {
    return typeof value === 'object' && value?.[key];
  }

  renderValue = (value: any) => {
    if (this.isSpecialType(value, 'isPhoto') && value.url) {
      return (
        <img
          src={value.url}
          style={{ height: '30px', cursor: 'pointer' }}
          onClick={(event) => this.handleImageClick(event, value.url)}
        />
      );
    }
    if (this.isSpecialType(value, 'isPdf') && value.url) {
      return (
        <a href={value.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          <PdfIcon style={{ fill: '#B00808' }} />
        </a>
      );
    }
    if (this.isSpecialType(value, 'isDate')) {
      return value.date;
    }
    if (typeof value === 'object' && value !== null && typeof value.text === 'string') {
      return value.text;
    }
    return value;
  }

  renderCategory = (name: string, value: any, deviceLabels: Array<[string, string]>) => {
    const { currentLabels } = this.props;

    if (typeof value !== 'object' || value === null) {
      return null;
    }

    const { isCategory, ...valueWithoutIsCategory } = value;

    const transformed = Object.entries(valueWithoutIsCategory).reduce((acc, [id, val]) => {
      const labelObj = deviceLabels.find(([labelId]) => labelId === id);
      const labelName = labelObj ? labelObj[1] : (currentLabels[id]?.details?.name || id);
      acc[labelName] = val;
      return acc;
    }, {} as Record<string, any>);

    const items = Object.entries(transformed)
      .map(([labelName, val]) => {
        let displayValue = '';
        if (typeof val === 'string') {
          displayValue = val;
        } else if (typeof val === 'object' && val !== null && 'text' in val && typeof (val as any).text === 'string') {
          displayValue = (val as any).text;
        } else {
          return null;
        }
        return (
          <div key={labelName} className={styles.infoItem}>
            <span className={styles.infoItem__label}>{labelName}: </span>
            <span className={styles.infoItem__value}>{displayValue}</span>
          </div>
        );
      })
      .filter(Boolean);

    return (
      <Collapsible
        header={<div>{name}</div>}
        isCollapsed={false}
        isCollapsible={false}
        className={styles.collapsible}
        headerClassName={styles.collapsibleHeader}
        bodyClassName={styles.collapsibleBody}
      >
        <>
          {items.length > 0 ? items : <div className={styles.infoItem}><span className={styles.infoItem__label}>None Yet</span></div>}
        </>
      </Collapsible>
    );
  }

  getSearchableValue = (value: unknown): string => {
    if (typeof value === 'string') return value.trim().toLowerCase();
    if (typeof value === 'object' && value !== null && typeof (value as any).text === 'string') {
      return (value as any).text.trim().toLowerCase();
    }
    return '';
  }

  getLabelName = (idOrKey: string, itemId: string, isDevice: boolean): string => {
    if (idOrKey === '0') return 'Serial Number';

    const { getLabelsForDevice, currentLabels } = this.props;
    if (isDevice) {
      const deviceLabels = getLabelsForDevice(itemId);
      const labelItem = deviceLabels.find(([labelId]) => labelId === idOrKey);
      if (labelItem && typeof labelItem[1] === 'string') {
        return labelItem[1];
      }
    }
    return currentLabels[idOrKey]?.details?.name || idOrKey;
  }

  getExtraInfoValueByLabelId = (
    extraInfo: Record<string, ExtraInfoValue>,
    labelId: string
  ): ExtraInfoValue | undefined => {
    // 1) top-level
    if (extraInfo[labelId] !== undefined) return extraInfo[labelId];

    // 2) inside any category
    for (const v of Object.values(extraInfo)) {
      if (isSpecialType(v, 'isCategory')) {
        const cat: any = v;
        if (cat[labelId] !== undefined) return cat[labelId];
      }
    }

    return undefined;
  }

  getVisibleSearchResults = () => {
    const {
      devices,
      people,
      tags,
      extraInfoDevices,
      extraInfoPeople,
      devicesLastPing,
      searchTerm,
    } = this.props;
    const {
      cachedDevices,
      cachedPeople,
      cachedTags,
      cachedExtraInfoDevices,
      cachedExtraInfoPeople,
      cachedDevicesLastPing,
      lastSearchTerm,
    } = this.state;

    const useCachedResults = lastSearchTerm === searchTerm;

    return {
      devices: useCachedResults && Object.keys(cachedDevices).length > 0 ? cachedDevices : devices,
      people: useCachedResults && Object.keys(cachedPeople).length > 0 ? cachedPeople : people,
      tags: useCachedResults && Object.keys(cachedTags).length > 0 ? cachedTags : tags,
      extraInfoDevices: useCachedResults && Object.keys(cachedExtraInfoDevices).length > 0 ? cachedExtraInfoDevices : extraInfoDevices,
      extraInfoPeople: useCachedResults && Object.keys(cachedExtraInfoPeople).length > 0 ? cachedExtraInfoPeople : extraInfoPeople,
      devicesLastPing: useCachedResults && cachedDevicesLastPing ? cachedDevicesLastPing : devicesLastPing,
    };
  }

  handleOpenInventoryReport = () => {
    const { dispatch, history } = this.props;
    const { devices, tags, extraInfoDevices } = this.getVisibleSearchResults();

    const deviceIds = Array.from(new Set([
      ...vals(devices).map(device => device.id),
      ...vals(extraInfoDevices).map(device => device.id),
    ]));
    const tagIds = vals(tags).map(tag => tag.details.id);

    dispatch(ReportActions.RESET_ALL_FILTERS());

    deviceIds.forEach(deviceId => {
      dispatch(ReportActions.TOGGLE_ITEM_FILTER('device', deviceId));
    });

    tagIds.forEach(tagId => {
      dispatch(ReportActions.TOGGLE_ITEM_FILTER('tag', tagId));
    });

    dispatch(GeneralActions.HIDE_SEARCH);
    dispatch(GeneralActions.HIDE_BOOKMARKS_SEARCH);
    history.push('/reports/new/inventory');
  }

  debugRender = () => {
    const {
      isSearchVis,
      isBookmarksSearchVis,
      dispatch,
      devicesDetails,
      searchTerm,
      searchBy,
      currentLabels,
      location,
      getLabelsForDevice
    } = this.props;
    const { gSearchResults, imageSrc } = this.state;
    const {
      devices,
      people,
      tags,
      extraInfoDevices,
      extraInfoPeople,
      devicesLastPing,
    } = this.getVisibleSearchResults();

    const rawSearchTerm = (this.props.searchTerm || '').trim();
    const term = rawSearchTerm.toLowerCase();

    const advancedConditions: AdvancedSearchCondition[] = rawSearchTerm.startsWith(ADVANCED_SEARCH_PREFIX)
      ? (() => {
        try {
          const parsed = JSON.parse(rawSearchTerm.slice(ADVANCED_SEARCH_PREFIX.length));
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      })()
      : [];

    const shouldHide = () => isMobile && dispatch(fullCloseDashboard());

    const naturalSort = (a, b, getValue = (item) => item.name, order = "asc") => {
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

      const valueA = getValue(a)?.trim().replace(/\s+/g, " ") || "";
      const valueB = getValue(b)?.trim().replace(/\s+/g, " ") || "";

      const comparison = collator.compare(valueA, valueB);

      return order === "asc" ? comparison : -comparison;
    };

    const isBookmarksBarSearchVisible = process.env.REACT_APP_SHOW_BOOKMARKS_BAR_SEARCH === 'true';
    const isSearchResultsBasedOnThePath = process.env.REACT_APP_SHOW_SEARCH_RESULTS_BASED_ON_THE_PATH === 'true';

    if (!isSearchVis && !isBookmarksSearchVis) return null;

    const getSearchOptionFromPath = (): SearchOptions => {
      if (location.pathname.includes('person')) return SearchOptions.PEOPLE;
      if (location.pathname.includes('device')) return SearchOptions.DEVICES;
      if (location.pathname.includes('tags')) return SearchOptions.TAGS;
      return SearchOptions.DEVICES;
    };

    const isSearchSectionShow = (
      searchOption: SearchOptions,
      searchBy: string,
      isSearchResultsBasedOnThePath: boolean,
      isBookmarksBarSearchVisible: boolean
    ): boolean => {
      const pathSearchOption = getSearchOptionFromPath();

      if (isBookmarksBarSearchVisible && isSearchResultsBasedOnThePath) {
        return searchBy === '' ? searchOption === pathSearchOption : searchBy === searchOption;
      }

      if (isBookmarksBarSearchVisible) {
        return searchBy === '' || searchBy === searchOption;
      }

      if (isSearchResultsBasedOnThePath) {
        return searchOption === pathSearchOption;
      }

      return true;
    };

    const hideSearch = () => {
      dispatch(GeneralActions.HIDE_SEARCH);
      dispatch(GeneralActions.HIDE_BOOKMARKS_SEARCH);
      dispatch(GeneralActions.CHANGE_TERM(''));
      dispatch(GeneralActions.CHANGE_SEARCH_BY(''));
    };

    return (
      <div className={`SearchModal ${styles.modalStyle}`} style={{ zIndex: isMobile ? 3 : 0 }} ref={this.searchModalRef}>
        <div style={{ padding: '16px 16px 88px 16px', overflowX: 'hidden' }}>
          {isSearchSectionShow(SearchOptions.DEVICES, searchBy, isSearchResultsBasedOnThePath, isBookmarksBarSearchVisible) && (<SearchCat
            header={process.env.REACT_APP_DEVICES_TAB_NAME || 'Devices'}
            links={vals(devices).sort((a, b) => naturalSort(a, b, item => item.name)).map((device) => {
              return {
                id: device.id,
                icon: <SvgIcon color='#111' icon={device.icon} />,
                link: `/device/${device.id}`,
              };
            })}
            renderItem={(deviceLinkItem) => (
              <Link
                to={`/device/${deviceLinkItem.id}`}
                onClick={hideSearch}
                style={{ width: '100%', height: '25px' }}
              >
                <DeviceListItem
                  deviceId={deviceLinkItem.id}
                  withIndicators={false}
                  iconSize={IconSize.SM_25}
                  withDeviceImage
                  subtext={devicesLastPing.getIn(
                    [deviceLinkItem.id, 'address', 'street'],
                    '') as string
                  }
                />
              </Link>
            )}
          />)}

          {isSearchSectionShow(SearchOptions.PEOPLE, searchBy, isSearchResultsBasedOnThePath, isBookmarksBarSearchVisible) && (<SearchCat
            header={process.env.REACT_APP_PEOPLE_TAB_NAME || 'People'}
            links={vals(people as iList<iPerson>).sort((a, b) => naturalSort(a, b, item => item.displayName)).map((person) => ({
              id: person.id,
              title: person.displayName,
              icon: <UserImage person={person} size={35} />,
              link: `/person/${person.id}`,
              assignedDevice: person.hasDevice ? extractDevice(
                devicesDetails,
                person.hasDevice?.deviceId
              ) : undefined,
              subText:
                devicesDetails.getIn([
                  path(['hasDevice', 'deviceId'])(person),
                  'address',
                  'street',
                ]) || '',
            }))}
          />)}

          {isSearchSectionShow(SearchOptions.TAGS, searchBy, isSearchResultsBasedOnThePath, isBookmarksBarSearchVisible) && (
            <SearchCat
              header={process.env.REACT_APP_TAGS_TAB_NAME || 'Tags'}
              links={vals(tags)
                .sort((a, b) => naturalSort(a, b, item => item.details.name))
                .map((tag) => ({
                  id: tag.details.id,
                  title: tag.details.name,
                  icon: (
                    <span
                      className={styles.pinIcon}
                      style={{ color: tag.details.color || '#111' }}
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24" className={styles.pinSvg}>
                        <path
                          fill="currentColor"
                          d="M12 2c-4.1 0-7.5 3.3-7.5 7.4 0 5.6 7.5 12.6 7.5 12.6S19.5 15 19.5 9.4C19.5 5.3 16.1 2 12 2z"
                        />
                      </svg>
                    </span>
                  ),
                  link: `/tag/${tag.details.id}`,
                  subText: '',
                }))}
            />
          )}

          {isSearchSectionShow(SearchOptions.MAP, searchBy, isSearchResultsBasedOnThePath, isBookmarksBarSearchVisible) && (<SearchCat
            header='Map'
            links={gSearchResults.map((res) => ({
              id: res.place_id,
              title: res.name || searchTerm,
              icon: (
                <SvgIcon
                  icon={{
                    type: IconTypes.UserSvgIcon,
                    url: 'images/place.svg',
                  }}
                  style={{ border: 'none', paddingTop: 5 }}
                />
              ),
              click: (pipe as any)(
                always(res),
                path(['geometry', 'location']),
                gstractLocation,
                this.recenterMap,
                always(res),
                GmapActions.ADD_SEARCH_MARKER,
                dispatch,
                shouldHide
              ),
              subText: res.formatted_address,
            }))}
          />)}

          {isSearchSectionShow(SearchOptions.DEVICES, searchBy, isSearchResultsBasedOnThePath, isBookmarksBarSearchVisible) && (
            <SearchCat
              header={'Information'}
              links={vals(extraInfoDevices)
                .sort((a, b) => naturalSort(a, b, (item) => item.name))
                .map((device) => {
                  const deviceLabels = getLabelsForDevice(device.id);

                  const extraInfo = device['extra-info'] || {};

                  const extraInfoEntries = advancedConditions.length
                    ? []
                    : Object.entries(extraInfo)
                      .filter(([_, value]) => {
                        const searchableValue = this.getSearchableValue(value);
                        const terms = term.split(' ').filter(Boolean);
                        return searchableValue && terms.some(t => searchableValue.includes(t));
                      });

                  const categorizedInfo = {};
                  const regularInfo = [];

                  if (advancedConditions.length) {
                    advancedConditions.forEach((condition) => {
                      const rawValue = this.getExtraInfoValueByLabelId(extraInfo as any, condition.fieldId);

                      if (rawValue === undefined || rawValue === null) return;

                      const label = this.getLabelName(condition.fieldId, device.id, true);
                      const displayValue = typeof rawValue === 'string'
                        ? rawValue
                        : (rawValue && typeof rawValue === 'object' && typeof (rawValue as any).text === 'string')
                          ? (rawValue as any).text
                          : String(rawValue);

                      regularInfo.push([label, displayValue]);
                    });
                  }

                  extraInfoEntries.forEach(([key, value]) => {
                    let labelName;

                    if (typeof value === 'object' && value['isCategory'] === true) {
                      const categoryLabelItem = deviceLabels.find(([id]) => id === key);
                      labelName = categoryLabelItem ? categoryLabelItem[0] : key;
                      categorizedInfo[labelName] = value;
                    } else {
                      const labelNameItem = deviceLabels.find(([id]) => id === key);
                      if (labelNameItem && typeof labelNameItem[1] === 'string') {
                        labelName = labelNameItem[1];
                      } else {
                        labelName = currentLabels[key as any]?.details?.name || key;
                      }

                      if (typeof labelName !== 'string') {
                        labelName = String(labelName);
                      }

                      let displayValue;
                      if (typeof value === 'string') {
                        displayValue = value;
                        regularInfo.push([labelName, displayValue]);
                      } else if (typeof value === 'object' && value !== null && typeof (value as any).text === 'string') {
                        displayValue = (value as any).text;
                        regularInfo.push([labelName, displayValue]);
                      }
                    }
                  });

                  const renderIcon = () => (
                    <SvgIcon size={IconSize.SM_25} color="#111" icon={device.icon} />
                  );

                  return {
                    id: device.id,
                    link: `/device/${device.id}`,
                    collapsibleContent: (
                      <SearchItemTile
                        title={device.name}
                        icon={renderIcon}
                        className={styles.searchTitleBody}
                        leftSide={{
                          link: `/device/${device.id}`,
                          onClick: hideSearch
                        }}
                        extraInfoValues={regularInfo}
                        renderValue={this.renderValue}
                        style={{
                          width: '100%',
                        }}
                      >
                        {Object.entries(categorizedInfo).map(([catName, catValue]) => (
                          <React.Fragment key={catName}>
                            {this.renderCategory(catName, catValue, deviceLabels)}
                          </React.Fragment>
                        ))}
                      </SearchItemTile>
                    ),
                  };
                })
              }
            />
          )}

          {isSearchSectionShow(SearchOptions.PEOPLE, searchBy, isSearchResultsBasedOnThePath, isBookmarksBarSearchVisible) && (<SearchCat
            header={'Information'}
            links={vals(extraInfoPeople).sort((a, b) => naturalSort(a, b, item => item.displayName)).map((person) => {
              const advancedSubtext = advancedConditions
                .map((condition) => {
                  const rawValue = this.getExtraInfoValueByLabelId((person['extra-info'] || {}) as any, condition.fieldId);
                  if (rawValue === undefined || rawValue === null) return null;

                  const label = currentLabels[condition.fieldId]?.details?.name || condition.fieldLabel || condition.fieldId;
                  const displayValue = typeof rawValue === 'string'
                    ? rawValue
                    : (rawValue && typeof rawValue === 'object' && typeof (rawValue as any).text === 'string')
                      ? (rawValue as any).text
                      : String(rawValue);

                  return (
                    <p key={condition.id || `${person.id}-${condition.fieldId}`}>
                      {`${label}: `}
                      {displayValue}
                    </p>
                  );
                })
                .filter(Boolean);

              return {
                id: person.id,
                title: person.displayName,
                icon: <UserImage person={person} size={35} />,
                link: `/person/${person.id}`,
                subText: advancedConditions.length ? advancedSubtext : Object.entries(person['extra-info'])
                  .filter(([key, value]) => {
                    const textValue = typeof value === 'string' ? value : (value as any)?.text;
                    return (
                      currentLabels[key]?.details?.name !== undefined &&
                      (typeof value === 'string' || (typeof value === 'object' && value !== null && 'text' in value && typeof (value as any).text === 'string')) &&
                      toLower(textValue).includes(searchTerm.toLowerCase())
                    );
                  })
                  .map(([key, value]) => {
                    let displayValue = '';
                    if (typeof value === 'string') {
                      displayValue = value;
                    } else if (typeof value === 'object' && value !== null && 'text' in value && typeof (value as any).text === 'string') {
                      displayValue = (value as any).text;
                    }
                    return (
                      <p key={key}>
                        {`${currentLabels[key]?.details?.name}: `}
                        {displayValue}
                      </p>
                    );
                  })
                  .filter(Boolean),
              };
            })}
          />)}
          {(vals(devices).length > 0 ||
            vals(people).length > 0 ||
            vals(tags).length > 0 ||
            vals(extraInfoDevices).length > 0 ||
            vals(extraInfoPeople).length > 0) && (
              <Button
                onClick={this.handleOpenInventoryReport}
                styleType={ButtonStyles.ORANGE_WHITE}
                size={ButtonSize.LG}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  width: '100%',
                }}
              >
                <Fa icon={faDownload} style={{ fontSize: '14px' }} />
                Inventory Report
              </Button>
          )}
        </div>
        {imageSrc && (
          <ModalImage
            closeImageHandler={this.handleResetImageClick}
            imageSrc={imageSrc}
          />
        )}
      </div>
    );
  };
}

export default connect(mapStateToProps)(withRouter(SearchModal));

interface iSearchCatProps {
  header: string;
  dispatch?: any;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  links: {
    id: string;
    title?: string;
    icon?: any;
    link?: string;
    click?: any;
    subText?: string | React.ReactNode;
    collapsibleContent?: React.ReactNode;
    assignedDevice?: iDeviceDetails | undefined;
    extraInfo?: string | React.ReactNode;
  }[];
  renderItem?: (link: any) => React.ReactNode;
}

const SearchCat = connect()(({ icon, header, links, dispatch, renderItem }: iSearchCatProps) => {
  const [isMapSearchResultVisible, setIsMapSearchResultVisible] = useState<boolean>(false);
  const isMapSearchCat = header === 'Map';
  const searchBy = useSelector<iFullStoreState, string>(state => state.general.searchBy);

  const hideSearch = () => {
    dispatch(GeneralActions.HIDE_SEARCH);
    dispatch(GeneralActions.HIDE_BOOKMARKS_SEARCH);
    dispatch(GeneralActions.CHANGE_TERM(''));
    dispatch(GeneralActions.CHANGE_SEARCH_BY(''));
  };

  const ItemWrapper = ({ title, linkTo, children }) => {
    if (title && linkTo) {
      return <Link to={linkTo} onClick={hideSearch}>
        {children}
      </Link>
    }
    else return <>{children}</>
  }

  return (
    <div style={{ marginBottom: 5 }}>
      <div className={styles.sectionHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {icon}
          <span>{header}</span>
        </div>
      </div>
      {((isMapSearchCat && !isMapSearchResultVisible && links.length > 0) && (searchBy !== SearchOptions.MAP)) && (
        <button
          style={{ color: 'rgb(255, 124, 2)', fontSize: '12px', padding: 0 }}
          onClick={() => setIsMapSearchResultVisible((prev) => !prev)}
        >
          Search on Map
        </button>
      )}
      {((isMapSearchCat && isMapSearchResultVisible) || !isMapSearchCat || searchBy === SearchOptions.MAP) && links.map((l) => {
        const action = l.link
          ? { to: l.link }
          : {
            to: '#',
            onClick: (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              l.click();
            },
          };
        return (
          <ItemWrapper title={l.title} linkTo={l.link} key={l.id}>
            <div className={styles.searchItem}>
              {renderItem ? renderItem(l) : (
                <>
                  {l.icon ? <span>{l.icon}</span> : null}
                  <div className={styles.titleAndSubtitlePart} style={{
                    ...(l.collapsibleContent && { width: '100%' }),
                  }}>
                    {l.title && <span className={styles.title}>{l.title}</span>}
                    {l.subText && <span className={styles.subtitle} style={{ ...(l.title && { fontSize: '9px' }) }}>{l.subText}</span>}
                    {l.collapsibleContent && <span className={styles.subtitle}>{l.collapsibleContent}</span>}
                    {l?.assignedDevice && <span className={styles.subtitle}>{l.assignedDevice.name}</span>}
                  </div>
                </>
              )}
            </div>
          </ItemWrapper>
        );
      })}
    </div>
  );
});